import {
    Box,
    Grid,
    Tabs,
    Tab,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from "@mui/material";
import { useState } from "react";
import { SandboxResult } from "../../../types/SandboxResult";
import SandboxResultDetail from "./SandboxResultDetail";
import SandboxItemList from "./SandboxItemList";
import SandboxDefinition from "../../../types/SandboxDefinition";
import { deleteSandbox, saveSandbox } from "../../../services/sandbox.service";
import { useNotifyMessage } from "../../../hooks/useNotifyMessage";
import CreateSandboxDialog from "./CreateSandboxDialog";
import SandboxEditor from "./SandboxEditor";

export default function Sandbox() {
    const { notifyMessage } = useNotifyMessage();

    const [script, setScript] = useState<string>("");
    const [sandboxResult, setSandboxResult] = useState<SandboxResult | null>(null);
    const [selectedItem, setSelectedItem] = useState<SandboxDefinition | null>(null);
    const [isNewSandboxDialogOpen, setIsNewSandboxDialogOpen] = useState<boolean>(false);
    const [doubleClickedItem, setdoubleClickedItem] = useState<SandboxDefinition | undefined>(undefined);

    const [resultsById, setResultsById] = useState<Record<string, SandboxResult>>({});
    const [activeTab, setActiveTab] = useState<number>(0); // 0 = Script, 1 = Result
    const [listReloadKey, setListReloadKey] = useState<number>(0);

    // Unsaved changes dialog state
    const [unsavedDialogOpen, setUnsavedDialogOpen] = useState<boolean>(false);
    const [pendingSelection, setPendingSelection] = useState<SandboxDefinition | null>(null);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const selectItem = (item: SandboxDefinition | null) => {
        setSelectedItem(item);
        setScript(item?.script || "");

        if (item) {
            setSandboxResult(resultsById[item.id] ?? null);
        } else {
            setSandboxResult(null);
        }
    };

    const resultRetrieved = (item: SandboxDefinition, result: SandboxResult) => {
        setResultsById(prev => ({
            ...prev,
            [item.id]: result
        }));

        if (selectedItem && item.id === selectedItem.id) {
            setSandboxResult(result);
        }
    };

    // Core save logic, returns a Promise so we can chain after dialog
    const doSave = () => {
        const sandboxToSave: SandboxDefinition =
            selectedItem
                ? { ...selectedItem, script }
                : { id: "", name: "New Sandbox", script: script };

        return saveSandbox(sandboxToSave).then(x => {
            if (x.status === 200 && x.data) {
                selectItem(x.data);
                setListReloadKey(prev => prev + 1);
                notifyMessage("Sandbox saved", "success");
            }
        });
    };

    const saveName = (name: string, definition?: SandboxDefinition) => {
        const isEdit = definition !== undefined;

        if (definition) {
            definition.name = name;
        } else {
            definition = { id: "", name: name, script: "" };
        }

        saveSandbox(definition).then(x => {
            if (x.status === 200 && x.data) {
                selectItem(x.data);
                setListReloadKey(prev => prev + 1);
                notifyMessage(isEdit ? "Sandbox updated" : "Sandbox created", "success");
            }
        });
    };

    const deleteItem = (item: SandboxDefinition) => {
        // If the deleted item is currently selected, clear the editor
        deleteSandbox(item.id).then(x => {
            if (x.status === 204) {
                setListReloadKey(prev => prev + 1);
                notifyMessage("Sandbox deleted", "success");
                if (selectedItem && item.id === selectedItem.id) {
                    selectItem(null);
                    setScript("");
                    setSandboxResult(null);
                }
            }
        });
    }

    const itemDoubleClick = (item: SandboxDefinition) => {
        setdoubleClickedItem(item);
        setIsNewSandboxDialogOpen(true);
    };

    const alterIsNewSandboxDialogOpen = (value: boolean) => {
        setdoubleClickedItem(undefined);
        setIsNewSandboxDialogOpen(value);
    };

    // Helper to check if the current script has unsaved changes
    const isDirty = (): boolean => {
        if (selectedItem) {
            return script !== (selectedItem.script || "");
        }
        // No selected item: treat non-empty script as dirty (new unsaved sandbox)
        return script.trim().length > 0;
    };

    // Called when user clicks an item in the list
    const handleItemSelected = (item: SandboxDefinition) => {
        // If the user clicks the already-selected item, do nothing
        if (selectedItem && item.id === selectedItem.id) {
            return;
        }

        if (!isDirty()) {
            // No unsaved changes, switch immediately
            selectItem(item);
            return;
        }

        // There are unsaved changes, show dialog and remember what the user wanted to select
        setPendingSelection(item);
        setUnsavedDialogOpen(true);
    };

    // Dialog handlers
    const handleUnsavedSave = () => {
        // Save current sandbox, then switch to pending selection
        doSave().then(() => {
            if (pendingSelection) {
                selectItem(pendingSelection);
            }
            setPendingSelection(null);
            setUnsavedDialogOpen(false);
        });
    };

    const handleUnsavedDiscard = () => {
        // Discard changes, just switch to pending selection
        if (pendingSelection) {
            selectItem(pendingSelection);
        }
        setPendingSelection(null);
        setUnsavedDialogOpen(false);
    };

    const handleUnsavedCancel = () => {
        // Stay on current sandbox
        setPendingSelection(null);
        setUnsavedDialogOpen(false);
    };

    return (
        <>
            <Grid
                container
                direction="column"
                spacing={1}
                sx={{ pt: 1 }}
            >
                <Grid item>
                    <Grid
                        container
                        spacing={1}
                    >
                        {/* Left: sandbox list + toolbar */}
                        <Grid
                            item
                            xs={12}
                            sm={4}
                            md={3}
                        >
                            <SandboxItemList
                                onSelected={handleItemSelected}
                                onResultRetrieved={resultRetrieved}
                                onItemDoubleClick={itemDoubleClick}
                                onAddClick={() => setIsNewSandboxDialogOpen(true)}
                                onSaveClick={() => doSave()}
                                onItemDelete={deleteItem}
                                selectedItemId={selectedItem?.id}
                                reloadKey={listReloadKey}
                            />
                        </Grid>

                        {/* Right: tabbed view (Script / Result) */}
                        <Grid
                            item
                            xs={12}
                            sm={8}
                            md={9}
                            sx={{
                                display: "flex",
                                flexDirection: "column"
                            }}
                        >
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ borderBottom: 1, borderColor: "divider" }}
                            >
                                <Tab label="Sandbox Script" />
                                <Tab label="Result" />
                            </Tabs>

                            <Box sx={{ mt: 1 }}>
                                {/* Script tab */}
                                {activeTab === 0 && (
                                    <Box>
                                        <SandboxEditor
                                            value={script}
                                            onChange={setScript}
                                            onSave={() => doSave()}  // Ctrl+S
                                        />
                                    </Box>
                                )}

                                {/* Result tab */}
                                {activeTab === 1 && (
                                    <Box sx={{ mt: 1 }}>
                                        {sandboxResult ? (
                                            <SandboxResultDetail result={sandboxResult} />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: 200,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "text.secondary"
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    No result available yet. Run a sandbox to see the result here.
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* New / rename dialog */}
            <CreateSandboxDialog
                isOpen={isNewSandboxDialogOpen}
                setIsOpen={alterIsNewSandboxDialogOpen}
                definition={doubleClickedItem}
                onSave={saveName}
            />

            {/* Unsaved changes confirmation dialog */}
            <Dialog
                open={unsavedDialogOpen}
                onClose={handleUnsavedCancel}
            >
                <DialogTitle>Unsaved changes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have unsaved changes in the current sandbox. Do you want to save them before switching?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUnsavedCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleUnsavedDiscard} color="warning">
                        Discard
                    </Button>
                    <Button onClick={handleUnsavedSave} color="primary" variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
